import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import type { OrderType } from "@/types/api/contracts/orders";
import type { VenueSpecific } from "@/types/api/venue";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { IAPI } from "./APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type {
  PaymentCardReadMethod,
  PaymentWalletType,
} from "@/components/screens/PastOrdersTransactions/Types/PastOrdersTransactionsTypes";

export enum PastTransactionsStatus {
  Completed = "completed",
  Refunded = "refunded",
  Failed = "failed",
  Pending = "pending",
}

export enum PaymentType {
  MobileCardPaymentIntent = 2,
  MobileCardPaymentPreAuth = 4,
  CardPayment = 5,
  CashPayment = 6,
}

export enum PastTransactionsCardType {
  Visa = "visa",
  Mastercard = "mastercard",
  AmericanExpress = "amex",
}

export interface ITransaction {
  created_at: string;
  updated_at: string;
  device: string;
  card_type: PastTransactionsCardType;
  payment_method: number;
  total_amount: number;
  id: ID;
  card_country_code: string;
  status: PastTransactionsStatus;
  card_last4: string;
  slot_id: ID;
  total_pre_refund_amount: number;
  offline_mode: boolean;
  payment_type: PaymentType;
  offline: boolean;
  location: string;
  order_type: OrderType;
  order_id: ID;
  order_identifier: ID;
  wallet_type: PaymentWalletType;
  card_read_method: PaymentCardReadMethod;
}

export interface ITransactionList extends APIPaginatedResponse<ITransaction> {}

export interface ITransactionParams extends VenueSpecific {
  status?: Array<PastTransactionsStatus>;
  order_type?: OrderType;
  redeemable_codes?: Array<ID>;
  offline?: boolean;
  slot_category_id?: Array<ID>;
  slot_id?: Array<ID>;
  payment_type?: Array<PaymentType>;
  last4?: string;
  amount_min?: string | number;
  amount_max?: string | number;
  start_date?: string;
  end_date?: string;
  page?: number;
  page_size?: number;
  sort?: string;
  payee_id?: ID;
}

export interface ITransactionService {
  list: (params: ITransactionParams) => UseQueryResult<ITransactionList, Error>;
  listByPayee: (payee_id: ID) => UseQueryResult<ITransactionList, Error>;
  exportTransactions: (
    params: ITransactionParams,
    enabled: boolean
  ) => UseQueryResult<string, Error>;
}

export class TransactionService implements ITransactionService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(params: ITransactionParams): UseQueryResult<ITransactionList, Error> {
    const { venueSpecific, ...restParams } = params;
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["transactions", "list"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async () => {
        const {
          data: { results, next, previous, count },
        } = await this.api.get<AxiosResponse<ITransactionList>>(
          "/bills/bill_payees/",
          {
            params: restParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: venueSpecific ?? false,
          }
        );

        return { results, next, previous, count };
      },
    });
  }

  listByPayee(payee_id: ID): UseQueryResult<ITransactionList, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["transactions", "list", "payee", payee_id],
        venueID: this.api.selectedVenue,
        params: {
          payee_id,
        },
      }),
      queryFn: async () => {
        const {
          data: { results, next, previous, count },
        } = await this.api.get<AxiosResponse<ITransactionList>>(
          "/bills/bill_payees/",
          {
            params: {
              payee_id,
            },
            endpointService: EndpointService.Gateway,
            venueSpecific: true,
          }
        );

        return { results, next, previous, count };
      },
      enabled: payee_id != null,
    });
  }

  exportTransactions(
    params: ITransactionParams,
    enabled: boolean
  ): UseQueryResult<string, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([_, value]) => value != null || value !== ""
      )
    );
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["transactions", "export"],
        venueID: this.api.selectedVenue,
        params: updatedParams,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<string>>(
          "/bills/bill_payees/csv/",
          {
            params: updatedParams,
            endpointService: EndpointService.Gateway,
            venueSpecific: params.venueSpecific ?? false,
          }
        );
        return data;
      },
      enabled,
    });
  }
}
